import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { Shield, Plus, Edit, Trash2, Users, FolderOpen, Eye, EyeOff, ClipboardCheck, Calendar, Settings, ExternalLink as ExternalLinkIcon, GripVertical } from 'lucide-react';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { format } from 'date-fns';
import { customerAPI, attendanceAPI, employeeAPI, userAPI, settingsAPI, externalLinksAPI } from '@/lib/api';

const AdminPanel = ({ user }) => {
  // Check if user is admin
  if (user?.role !== 'admin') {
    return (
      <Card className="max-w-2xl mx-auto mt-20">
        <CardContent className="p-12 text-center">
          <Shield className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
          <h2 className="text-2xl font-bold mb-2">Access Denied</h2>
          <p className="text-muted-foreground">You don't have permission to access the admin panel.</p>
        </CardContent>
      </Card>
    );
  }

  const [users, setUsers] = useState([]);

  const [categories, setCategories] = useState([
    { id: 1, name: 'Home', description: 'Dashboard home page', enabled: true },
    { id: 2, name: 'Attendance', description: 'Staff attendance management', enabled: true },
    { id: 3, name: 'Gold Calculator', description: 'Calculate gold prices', enabled: true },
    { id: 4, name: 'Order Status', description: 'Track customer orders', enabled: true },
    { id: 5, name: 'To-do List', description: 'Task management', enabled: true },
    { id: 6, name: 'Stocks/Indent', description: 'Inventory management', enabled: true },
    { id: 7, name: 'Checklists', description: 'Daily/Weekly/Monthly checklists', enabled: true }
  ]);

  // Define available modules with granular permissions
  const availableModules = [
    { key: 'gold-calculator', label: 'Gold Calculator' },
    { key: 'attendance', label: 'Attendance' },
    { key: 'address-book', label: 'Address Book' },
    { key: 'cad', label: 'CAD Orders' },
    { key: 'loss-cal', label: 'Loss Cal' },
    { key: 'estimate', label: 'Estimate' },
    { key: 'defaults', label: 'Defaults' },
    { key: 'remainder', label: 'Remainder' },
    { key: 'order-status', label: 'Order Status' },
    { key: 'stocks-indent', label: 'Stocks/Indent' },
    { key: 'checklists', label: 'Checklists' },
    { key: 'reports', label: 'Reports' },
    { key: 'admin-panel', label: 'Admin Panel' }
  ];

  const initializeModulePermissions = () => {
    const perms = {};
    availableModules.forEach(module => {
      perms[module.key] = { access: false, add: false, edit: false, delete: false };
    });
    return perms;
  };

  const [newUser, setNewUser] = useState({ 
    username: '', 
    password: '', 
    role: 'user', 
    permissions: [],
    module_permissions: initializeModulePermissions()
  });
  const [editingUser, setEditingUser] = useState(null);
  const [isAddUserOpen, setIsAddUserOpen] = useState(false);
  const [isEditUserOpen, setIsEditUserOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);
  const [changingPasswordUser, setChangingPasswordUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');

  const [newCategory, setNewCategory] = useState({ name: '', description: '' });
  const [editingCategory, setEditingCategory] = useState(null);
  const [isAddCategoryOpen, setIsAddCategoryOpen] = useState(false);
  const [isEditCategoryOpen, setIsEditCategoryOpen] = useState(false);

  // Customer Management State
  const [customers, setCustomers] = useState([]);
  const [newCustomer, setNewCustomer] = useState({ name: '', phone: '', email: '', address: '' });
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [isAddCustomerOpen, setIsAddCustomerOpen] = useState(false);
  const [isEditCustomerOpen, setIsEditCustomerOpen] = useState(false);
  const [isLoadingCustomers, setIsLoadingCustomers] = useState(false);

  // Attendance Management State
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [isLoadingAttendance, setIsLoadingAttendance] = useState(false);
  const [editingAttendance, setEditingAttendance] = useState(null);
  const [isEditAttendanceOpen, setIsEditAttendanceOpen] = useState(false);

  // App Settings State
  const [appSettings, setAppSettings] = useState({
    custom_link_label: '',
    custom_link_url: ''
  });
  const [isLoadingSettings, setIsLoadingSettings] = useState(false);

  // External Links Management State
  const [externalLinks, setExternalLinks] = useState([]);
  const [isLoadingExternalLinks, setIsLoadingExternalLinks] = useState(false);
  const [newExternalLink, setNewExternalLink] = useState({ label: '', url: '', icon: 'Filter', position: 0, enabled: true });
  const [editingExternalLink, setEditingExternalLink] = useState(null);
  const [isAddExternalLinkOpen, setIsAddExternalLinkOpen] = useState(false);
  const [isEditExternalLinkOpen, setIsEditExternalLinkOpen] = useState(false);
  const [draggedLinkIndex, setDraggedLinkIndex] = useState(null);
  const [dragOverLinkIndex, setDragOverLinkIndex] = useState(null);

  // Load data from backend on component mount
  useEffect(() => {
    loadUsers();
    loadCustomers();
    loadEmployees();
    loadSettings();
    loadExternalLinks();
  }, []);

  // Load attendance when date changes
  useEffect(() => {
    if (selectedDate) {
      loadAttendance();
    }
  }, [selectedDate]);

  const loadEmployees = async () => {
    try {
      const data = await employeeAPI.getAll();
      setEmployees(data);
    } catch (error) {
      console.error('Error loading employees:', error);
    }
  };

  const loadAttendance = async () => {
    setIsLoadingAttendance(true);
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const data = await attendanceAPI.getAll(dateStr);
      setAttendanceRecords(data);
    } catch (error) {
      toast.error('Failed to load attendance records');
      console.error('Error loading attendance:', error);
    } finally {
      setIsLoadingAttendance(false);
    }
  };

  const handleEditAttendance = async () => {
    if (!editingAttendance || !editingAttendance.status) {
      toast.error('Please select a status');
      return;
    }

    try {
      const updated = await attendanceAPI.update(editingAttendance.id, {
        status: editingAttendance.status,
        notes: editingAttendance.notes
      });
      setAttendanceRecords(attendanceRecords.map(a => a.id === editingAttendance.id ? updated : a));
      setIsEditAttendanceOpen(false);
      setEditingAttendance(null);
      toast.success('Attendance updated successfully');
    } catch (error) {
      toast.error('Failed to update attendance');
      console.error('Error updating attendance:', error);
    }
  };

  const handleDeleteAttendance = async (id) => {
    if (!window.confirm('Are you sure you want to delete this attendance record?')) return;

    try {
      await attendanceAPI.delete(id);
      setAttendanceRecords(attendanceRecords.filter(a => a.id !== id));
      toast.success('Attendance record deleted');
    } catch (error) {
      toast.error('Failed to delete attendance');
      console.error('Error deleting attendance:', error);
    }
  };

  const getAttendanceBadge = (status) => {
    const variants = {
      'Present': 'default',
      'Absent': 'destructive',
      'Leave': 'secondary',
      'Half-day': 'outline'
    };
    return <Badge variant={variants[status] || 'outline'}>{status}</Badge>;
  };

  const loadCustomers = async () => {
    setIsLoadingCustomers(true);
    try {
      const data = await customerAPI.getAll();
      setCustomers(data);
    } catch (error) {
      toast.error('Failed to load customers');
      console.error('Error loading customers:', error);
    } finally {
      setIsLoadingCustomers(false);
    }
  };

  const availablePermissions = [
    'attendance', 'address-book', 'cad', 'gold-calculator', 'order-status', 'todo-list',
    'stocks-indent', 'daily-checklist', 'weekly-checklist', 'monthly-checklist', 'reports'
  ];

  // Helper function to handle module permission changes
  const handleModulePermissionChange = (moduleKey, permissionType, value, isNewUser = true) => {
    if (isNewUser) {
      setNewUser(prev => ({
        ...prev,
        module_permissions: {
          ...prev.module_permissions,
          [moduleKey]: {
            ...prev.module_permissions[moduleKey],
            [permissionType]: value
          }
        }
      }));
    } else {
      setEditingUser(prev => ({
        ...prev,
        module_permissions: {
          ...prev.module_permissions,
          [moduleKey]: {
            ...prev.module_permissions[moduleKey],
            [permissionType]: value
          }
        }
      }));
    }
  };

  // Load users from backend
  const loadUsers = async () => {
    setIsLoadingUsers(true);
    try {
      const data = await userAPI.getAll();
      // Ensure module_permissions exists for each user
      const usersWithPerms = data.map(u => ({
        ...u,
        module_permissions: u.module_permissions || initializeModulePermissions()
      }));
      setUsers(usersWithPerms);
    } catch (error) {
      toast.error('Failed to load users');
      console.error('Error loading users:', error);
    } finally {
      setIsLoadingUsers(false);
    }
  };

  // User Management Functions
  const handleAddUser = async () => {
    if (!newUser.username || !newUser.password) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const createdUser = await userAPI.create(newUser);
      setUsers([...users, createdUser]);
      setNewUser({ 
        username: '', 
        password: '', 
        role: 'user', 
        permissions: [],
        module_permissions: initializeModulePermissions()
      });
      setIsAddUserOpen(false);
      toast.success('User added successfully');
    } catch (error) {
      toast.error(error.message || 'Failed to add user');
      console.error('Error adding user:', error);
    }
  };

  const handleEditUser = async () => {
    if (!editingUser.username) {
      toast.error('Username is required');
      return;
    }

    try {
      const updated = await userAPI.update(editingUser.id, {
        username: editingUser.username,
        role: editingUser.role,
        permissions: editingUser.permissions
      });
      setUsers(users.map(u => u.id === editingUser.id ? updated : u));
      setIsEditUserOpen(false);
      setEditingUser(null);
      toast.success('User updated successfully');
    } catch (error) {
      toast.error(error.message || 'Failed to update user');
      console.error('Error updating user:', error);
    }
  };

  const handleDeleteUser = async (id) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;

    try {
      await userAPI.delete(id);
      setUsers(users.filter(u => u.id !== id));
      toast.success('User deleted successfully');
    } catch (error) {
      toast.error(error.message || 'Failed to delete user');
      console.error('Error deleting user:', error);
    }
  };

  // Settings Functions
  const loadSettings = async () => {
    setIsLoadingSettings(true);
    try {
      const data = await settingsAPI.get();
      setAppSettings({
        custom_link_label: data.custom_link_label || '',
        custom_link_url: data.custom_link_url || ''
      });
    } catch (error) {
      console.error('Error loading settings:', error);
    } finally {
      setIsLoadingSettings(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      await settingsAPI.update(appSettings);
      toast.success('Settings saved successfully');
    } catch (error) {
      toast.error('Failed to save settings');
      console.error('Error saving settings:', error);
    }
  };

  // External Links Management Functions
  const broadcastExternalLinksChange = () => {
    // Notify same-tab listeners (Layout sidebar) instantly
    window.dispatchEvent(new Event('external-links-updated'));
    // Notify other tabs in the same browser via storage event
    try {
      localStorage.setItem('external_links_updated_at', String(Date.now()));
    } catch (e) {
      // localStorage may be unavailable in some contexts; ignore
    }
  };

  const loadExternalLinks = async () => {
    setIsLoadingExternalLinks(true);
    try {
      const data = await externalLinksAPI.list();
      setExternalLinks(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error loading external links:', error);
    } finally {
      setIsLoadingExternalLinks(false);
    }
  };

  const handleAddExternalLink = async () => {
    if (!newExternalLink.label.trim() || !newExternalLink.url.trim()) {
      toast.error('Label and URL are required');
      return;
    }
    if (!/^https?:\/\//i.test(newExternalLink.url.trim())) {
      toast.error('URL must start with http:// or https://');
      return;
    }
    try {
      await externalLinksAPI.create(newExternalLink);
      toast.success('External link added — sidebar updated.');
      setIsAddExternalLinkOpen(false);
      setNewExternalLink({ label: '', url: '', icon: 'Filter', position: 0, enabled: true });
      loadExternalLinks();
      broadcastExternalLinksChange();
    } catch (error) {
      toast.error(error.message || 'Failed to add external link');
    }
  };

  const handleUpdateExternalLink = async () => {
    if (!editingExternalLink) return;
    if (!editingExternalLink.label.trim() || !editingExternalLink.url.trim()) {
      toast.error('Label and URL are required');
      return;
    }
    if (!/^https?:\/\//i.test(editingExternalLink.url.trim())) {
      toast.error('URL must start with http:// or https://');
      return;
    }
    try {
      await externalLinksAPI.update(editingExternalLink.id, {
        label: editingExternalLink.label,
        url: editingExternalLink.url,
        icon: editingExternalLink.icon,
        position: editingExternalLink.position,
        enabled: editingExternalLink.enabled,
      });
      toast.success('External link updated — sidebar updated.');
      setIsEditExternalLinkOpen(false);
      setEditingExternalLink(null);
      loadExternalLinks();
      broadcastExternalLinksChange();
    } catch (error) {
      toast.error(error.message || 'Failed to update external link');
    }
  };

  const handleDeleteExternalLink = async (linkId) => {
    if (!window.confirm('Delete this external link? Users will lose access immediately.')) return;
    try {
      await externalLinksAPI.delete(linkId);
      toast.success('External link deleted');
      loadExternalLinks();
      broadcastExternalLinksChange();
    } catch (error) {
      toast.error(error.message || 'Failed to delete external link');
    }
  };

  // Drag-and-drop reordering for external links
  const handleLinkDragStart = (index) => {
    setDraggedLinkIndex(index);
  };

  const handleLinkDragOver = (e, index) => {
    e.preventDefault();
    if (index !== dragOverLinkIndex) {
      setDragOverLinkIndex(index);
    }
  };

  const handleLinkDragEnd = () => {
    setDraggedLinkIndex(null);
    setDragOverLinkIndex(null);
  };

  const handleLinkDrop = async (e, dropIndex) => {
    e.preventDefault();
    if (draggedLinkIndex === null || draggedLinkIndex === dropIndex) {
      handleLinkDragEnd();
      return;
    }

    // Reorder array
    const reordered = [...externalLinks];
    const [moved] = reordered.splice(draggedLinkIndex, 1);
    reordered.splice(dropIndex, 0, moved);

    // Assign new positions
    const updates = reordered.map((link, idx) => ({ ...link, position: idx }));
    setExternalLinks(updates);
    handleLinkDragEnd();

    // Persist position changes for items whose position changed
    try {
      await Promise.all(
        updates
          .filter((link, idx) => externalLinks[idx]?.id !== link.id || externalLinks[idx]?.position !== link.position)
          .map(link => externalLinksAPI.update(link.id, { position: link.position }))
      );
      toast.success('Order saved — sidebar updated.');
      loadExternalLinks();
      broadcastExternalLinksChange();
    } catch (error) {
      toast.error('Failed to save new order');
      loadExternalLinks();
    }
  };

  const handleChangePassword = async () => {
    if (!newPassword || newPassword.length < 4) {
      toast.error('Password must be at least 4 characters');
      return;
    }

    try {
      await userAPI.changePassword(changingPasswordUser.id, newPassword);
      setIsChangePasswordOpen(false);
      setChangingPasswordUser(null);
      setNewPassword('');
      toast.success('Password changed successfully');
    } catch (error) {
      toast.error(error.message || 'Failed to change password');
      console.error('Error changing password:', error);
    }
  };

  // Category Management Functions
  const handleAddCategory = () => {
    if (!newCategory.name) {
      toast.error('Category name is required');
      return;
    }

    const category = {
      id: Date.now(),
      ...newCategory,
      enabled: true
    };

    setCategories([...categories, category]);
    setNewCategory({ name: '', description: '' });
    setIsAddCategoryOpen(false);
    toast.success('Category added successfully');
  };

  const handleEditCategory = () => {
    if (!editingCategory.name) {
      toast.error('Category name is required');
      return;
    }

    setCategories(categories.map(c => c.id === editingCategory.id ? editingCategory : c));
    setIsEditCategoryOpen(false);
    setEditingCategory(null);
    toast.success('Category updated successfully');
  };

  const handleDeleteCategory = (id) => {
    setCategories(categories.filter(c => c.id !== id));
    toast.success('Category deleted successfully');
  };

  const handleToggleCategoryStatus = (id) => {
    setCategories(categories.map(c => c.id === id ? { ...c, enabled: !c.enabled } : c));
    toast.success('Category status updated');
  };

  // Customer Management Functions
  const handleAddCustomer = async () => {
    if (!newCustomer.name.trim()) {
      toast.error('Customer name is required');
      return;
    }
    
    try {
      const customer = await customerAPI.create(newCustomer);
      setCustomers([...customers, customer]);
      setNewCustomer({ name: '', phone: '', email: '', address: '' });
      setIsAddCustomerOpen(false);
      toast.success('Customer added successfully');
    } catch (error) {
      toast.error('Failed to add customer');
      console.error('Error adding customer:', error);
    }
  };

  const handleEditCustomer = async () => {
    if (!editingCustomer.name.trim()) {
      toast.error('Customer name is required');
      return;
    }
    
    try {
      const updated = await customerAPI.update(editingCustomer.id, {
        name: editingCustomer.name,
        phone: editingCustomer.phone,
        email: editingCustomer.email,
        address: editingCustomer.address
      });
      setCustomers(customers.map(c => c.id === editingCustomer.id ? updated : c));
      setIsEditCustomerOpen(false);
      setEditingCustomer(null);
      toast.success('Customer updated successfully');
    } catch (error) {
      toast.error('Failed to update customer');
      console.error('Error updating customer:', error);
    }
  };

  const handleDeleteCustomer = async (id) => {
    try {
      await customerAPI.delete(id);
      setCustomers(customers.filter(c => c.id !== id));
      toast.success('Customer deleted successfully');
    } catch (error) {
      toast.error('Failed to delete customer');
      console.error('Error deleting customer:', error);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center gap-3">
        <div className="p-3 rounded-lg bg-primary/10">
          <Shield className="h-6 w-6 text-primary" />
        </div>
        <div>
          <h1 className="text-3xl font-bold">Admin Panel</h1>
          <p className="text-muted-foreground mt-1">Manage users, categories, and permissions</p>
        </div>
      </div>

      <Tabs defaultValue="users" className="space-y-6">
        <TabsList className="grid w-full grid-cols-6 max-w-5xl">
          <TabsTrigger value="users">
            <Users className="h-4 w-4 mr-2" />
            Users
          </TabsTrigger>
          <TabsTrigger value="customers">
            <Users className="h-4 w-4 mr-2" />
            Customers
          </TabsTrigger>
          <TabsTrigger value="attendance">
            <ClipboardCheck className="h-4 w-4 mr-2" />
            Attendance
          </TabsTrigger>
          <TabsTrigger value="categories">
            <FolderOpen className="h-4 w-4 mr-2" />
            Categories
          </TabsTrigger>
          <TabsTrigger value="external-links" data-testid="tab-external-links">
            <ExternalLinkIcon className="h-4 w-4 mr-2" />
            External Links
          </TabsTrigger>
          <TabsTrigger value="settings">
            <Settings className="h-4 w-4 mr-2" />
            Settings
          </TabsTrigger>
        </TabsList>

        {/* User Management Tab */}
        <TabsContent value="users" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>User Management</CardTitle>
                  <CardDescription>Add, edit, or remove user accounts</CardDescription>
                </div>
                <Dialog open={isAddUserOpen} onOpenChange={setIsAddUserOpen}>
                  <DialogTrigger asChild>
                    <Button className="shadow-emerald">
                      <Plus className="h-4 w-4 mr-2" />
                      Add User
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-xl">
                    <DialogHeader>
                      <DialogTitle>Add New User</DialogTitle>
                      <DialogDescription>Create a new user account</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="new-username">Username</Label>
                          <Input
                            id="new-username"
                            placeholder="Enter username"
                            value={newUser.username}
                            onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="new-password">Password</Label>
                          <div className="relative">
                            <Input
                              id="new-password"
                              type={showPassword ? 'text' : 'password'}
                              placeholder="Enter password"
                              value={newUser.password}
                              onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                              className="pr-10"
                            />
                            <button
                              type="button"
                              onClick={() => setShowPassword(!showPassword)}
                              className="absolute right-3 top-1/2 -translate-y-1/2"
                            >
                              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </button>
                          </div>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="new-role">Role</Label>
                        <Select value={newUser.role} onValueChange={(value) => setNewUser({ ...newUser, role: value })}>
                          <SelectTrigger id="new-role">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="admin">Admin</SelectItem>
                            <SelectItem value="manager">Manager</SelectItem>
                            <SelectItem value="user">User</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-3">
                        <Label>Module Permissions</Label>
                        <div className="max-h-64 overflow-y-auto border rounded-lg">
                          <table className="w-full text-sm">
                            <thead className="bg-muted sticky top-0">
                              <tr>
                                <th className="text-left p-2 font-medium">Module</th>
                                <th className="text-center p-2 font-medium w-20">Access</th>
                                <th className="text-center p-2 font-medium w-20">Add</th>
                                <th className="text-center p-2 font-medium w-20">Edit</th>
                                <th className="text-center p-2 font-medium w-20">Delete</th>
                              </tr>
                            </thead>
                            <tbody>
                              {availableModules.map(module => (
                                <tr key={module.key} className="border-b hover:bg-muted/50">
                                  <td className="p-2">{module.label}</td>
                                  <td className="text-center p-2">
                                    <input
                                      type="checkbox"
                                      checked={newUser.module_permissions[module.key]?.access || false}
                                      onChange={(e) => handleModulePermissionChange(module.key, 'access', e.target.checked, true)}
                                      className="rounded border-gray-300 cursor-pointer"
                                    />
                                  </td>
                                  <td className="text-center p-2">
                                    <input
                                      type="checkbox"
                                      checked={newUser.module_permissions[module.key]?.add || false}
                                      onChange={(e) => handleModulePermissionChange(module.key, 'add', e.target.checked, true)}
                                      disabled={!newUser.module_permissions[module.key]?.access}
                                      className="rounded border-gray-300 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                    />
                                  </td>
                                  <td className="text-center p-2">
                                    <input
                                      type="checkbox"
                                      checked={newUser.module_permissions[module.key]?.edit || false}
                                      onChange={(e) => handleModulePermissionChange(module.key, 'edit', e.target.checked, true)}
                                      disabled={!newUser.module_permissions[module.key]?.access}
                                      className="rounded border-gray-300 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                    />
                                  </td>
                                  <td className="text-center p-2">
                                    <input
                                      type="checkbox"
                                      checked={newUser.module_permissions[module.key]?.delete || false}
                                      onChange={(e) => handleModulePermissionChange(module.key, 'delete', e.target.checked, true)}
                                      disabled={!newUser.module_permissions[module.key]?.access}
                                      className="rounded border-gray-300 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                    />
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Note: Add, Edit, and Delete permissions require Access to be enabled first.
                        </p>
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setIsAddUserOpen(false)}>Cancel</Button>
                      <Button onClick={handleAddUser}>Add User</Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              {isLoadingUsers ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
                  <p className="text-sm text-muted-foreground">Loading users...</p>
                </div>
              ) : users.length === 0 ? (
                <div className="text-center py-12">
                  <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-lg font-medium mb-2">No users found</p>
                  <p className="text-muted-foreground">Click "Add User" to create a new user</p>
                </div>
              ) : (
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Username</TableHead>
                        <TableHead>Role</TableHead>
                        <TableHead>Permissions</TableHead>
                        <TableHead>Created Date</TableHead>
                        <TableHead>Role Badge</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {users.map((user) => (
                      <TableRow key={user.id}>
                        <TableCell className="font-medium">{user.username}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="capitalize">{user.role}</Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {user.permissions.slice(0, 2).map((perm, idx) => (
                              <Badge key={idx} variant="secondary" className="text-xs">{perm}</Badge>
                            ))}
                            {user.permissions.length > 2 && (
                              <Badge variant="secondary" className="text-xs">+{user.permissions.length - 2}</Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {user.created_at ? new Date(user.created_at).toLocaleDateString() : '-'}
                        </TableCell>
                        <TableCell>
                          <Badge variant={user.role === 'admin' ? 'default' : 'secondary'}>
                            {user.role}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setChangingPasswordUser(user);
                                setIsChangePasswordOpen(true);
                              }}
                            >
                              Change Password
                            </Button>
                            <Dialog open={isEditUserOpen && editingUser?.id === user.id} onOpenChange={(open) => {
                              setIsEditUserOpen(open);
                              if (!open) setEditingUser(null);
                            }}>
                              <DialogTrigger asChild>
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  onClick={() => setEditingUser({
                                    ...user,
                                    module_permissions: user.module_permissions || initializeModulePermissions()
                                  })}
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                              </DialogTrigger>
                              <DialogContent className="max-w-xl">
                                <DialogHeader>
                                  <DialogTitle>Edit User</DialogTitle>
                                  <DialogDescription>Update user details and permissions</DialogDescription>
                                </DialogHeader>
                                {editingUser && (
                                  <div className="space-y-4 py-4">
                                    <div className="space-y-2">
                                      <Label htmlFor="edit-username">Username</Label>
                                      <Input
                                        id="edit-username"
                                        value={editingUser.username}
                                        onChange={(e) => setEditingUser({ ...editingUser, username: e.target.value })}
                                      />
                                    </div>
                                    <div className="space-y-2">
                                      <Label htmlFor="edit-role">Role</Label>
                                      <Select value={editingUser.role} onValueChange={(value) => setEditingUser({ ...editingUser, role: value })}>
                                        <SelectTrigger id="edit-role">
                                          <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                          <SelectItem value="admin">Admin</SelectItem>
                                          <SelectItem value="manager">Manager</SelectItem>
                                          <SelectItem value="user">User</SelectItem>
                                        </SelectContent>
                                      </Select>
                                    </div>
                                    <div className="space-y-3">
                                      <Label>Module Permissions</Label>
                                      <div className="max-h-64 overflow-y-auto border rounded-lg">
                                        <table className="w-full text-sm">
                                          <thead className="bg-muted sticky top-0">
                                            <tr>
                                              <th className="text-left p-2 font-medium">Module</th>
                                              <th className="text-center p-2 font-medium w-20">Access</th>
                                              <th className="text-center p-2 font-medium w-20">Add</th>
                                              <th className="text-center p-2 font-medium w-20">Edit</th>
                                              <th className="text-center p-2 font-medium w-20">Delete</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {availableModules.map(module => (
                                              <tr key={module.key} className="border-b hover:bg-muted/50">
                                                <td className="p-2">{module.label}</td>
                                                <td className="text-center p-2">
                                                  <input
                                                    type="checkbox"
                                                    checked={editingUser.module_permissions?.[module.key]?.access || false}
                                                    onChange={(e) => handleModulePermissionChange(module.key, 'access', e.target.checked, false)}
                                                    className="rounded border-gray-300 cursor-pointer"
                                                  />
                                                </td>
                                                <td className="text-center p-2">
                                                  <input
                                                    type="checkbox"
                                                    checked={editingUser.module_permissions?.[module.key]?.add || false}
                                                    onChange={(e) => handleModulePermissionChange(module.key, 'add', e.target.checked, false)}
                                                    disabled={!editingUser.module_permissions?.[module.key]?.access}
                                                    className="rounded border-gray-300 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                                  />
                                                </td>
                                                <td className="text-center p-2">
                                                  <input
                                                    type="checkbox"
                                                    checked={editingUser.module_permissions?.[module.key]?.edit || false}
                                                    onChange={(e) => handleModulePermissionChange(module.key, 'edit', e.target.checked, false)}
                                                    disabled={!editingUser.module_permissions?.[module.key]?.access}
                                                    className="rounded border-gray-300 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                                  />
                                                </td>
                                                <td className="text-center p-2">
                                                  <input
                                                    type="checkbox"
                                                    checked={editingUser.module_permissions?.[module.key]?.delete || false}
                                                    onChange={(e) => handleModulePermissionChange(module.key, 'delete', e.target.checked, false)}
                                                    disabled={!editingUser.module_permissions?.[module.key]?.access}
                                                    className="rounded border-gray-300 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                                  />
                                                </td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      </div>
                                      <p className="text-xs text-muted-foreground">
                                        Note: Add, Edit, and Delete permissions require Access to be enabled first.
                                      </p>
                                    </div>
                                  </div>
                                )}
                                <DialogFooter>
                                  <Button variant="outline" onClick={() => setIsEditUserOpen(false)}>Cancel</Button>
                                  <Button onClick={handleEditUser}>Save Changes</Button>
                                </DialogFooter>
                              </DialogContent>
                            </Dialog>
                            <Button
                              size="icon"
                              variant="ghost"
                              onClick={() => handleDeleteUser(user.id)}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Customer Management Tab */}
        <TabsContent value="customers" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Customer Management</CardTitle>
                  <CardDescription>Add, edit, or remove customers</CardDescription>
                </div>
                <Dialog open={isAddCustomerOpen} onOpenChange={setIsAddCustomerOpen}>
                  <DialogTrigger asChild>
                    <Button className="shadow-emerald">
                      <Plus className="h-4 w-4 mr-2" />
                      Add Customer
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Add New Customer</DialogTitle>
                      <DialogDescription>Create a new customer record</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="space-y-2">
                        <Label htmlFor="new-customer-name">Customer Name *</Label>
                        <Input
                          id="new-customer-name"
                          placeholder="Enter customer name"
                          value={newCustomer.name}
                          onChange={(e) => setNewCustomer({ ...newCustomer, name: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="new-customer-phone">Phone Number</Label>
                        <Input
                          id="new-customer-phone"
                          placeholder="Enter phone number"
                          value={newCustomer.phone}
                          onChange={(e) => setNewCustomer({ ...newCustomer, phone: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="new-customer-email">Email</Label>
                        <Input
                          id="new-customer-email"
                          type="email"
                          placeholder="Enter email address"
                          value={newCustomer.email}
                          onChange={(e) => setNewCustomer({ ...newCustomer, email: e.target.value })}
                        />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setIsAddCustomerOpen(false)}>Cancel</Button>
                      <Button onClick={handleAddCustomer}>Add Customer</Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Customer Name</TableHead>
                      <TableHead>Phone Number</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {customers.map((customer) => (
                      <TableRow key={customer.id}>
                        <TableCell className="font-medium">{customer.name}</TableCell>
                        <TableCell>{customer.phone || '-'}</TableCell>
                        <TableCell>{customer.email || '-'}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Dialog open={isEditCustomerOpen && editingCustomer?.id === customer.id} onOpenChange={(open) => {
                              setIsEditCustomerOpen(open);
                              if (!open) setEditingCustomer(null);
                            }}>
                              <DialogTrigger asChild>
                                <Button size="icon" variant="ghost" onClick={() => setEditingCustomer(customer)}>
                                  <Edit className="h-4 w-4" />
                                </Button>
                              </DialogTrigger>
                              <DialogContent>
                                <DialogHeader>
                                  <DialogTitle>Edit Customer</DialogTitle>
                                  <DialogDescription>Update customer details</DialogDescription>
                                </DialogHeader>
                                {editingCustomer && (
                                  <div className="space-y-4 py-4">
                                    <div className="space-y-2">
                                      <Label htmlFor="edit-customer-name">Customer Name *</Label>
                                      <Input
                                        id="edit-customer-name"
                                        value={editingCustomer.name}
                                        onChange={(e) => setEditingCustomer({ ...editingCustomer, name: e.target.value })}
                                      />
                                    </div>
                                    <div className="space-y-2">
                                      <Label htmlFor="edit-customer-phone">Phone Number</Label>
                                      <Input
                                        id="edit-customer-phone"
                                        value={editingCustomer.phone}
                                        onChange={(e) => setEditingCustomer({ ...editingCustomer, phone: e.target.value })}
                                      />
                                    </div>
                                    <div className="space-y-2">
                                      <Label htmlFor="edit-customer-email">Email</Label>
                                      <Input
                                        id="edit-customer-email"
                                        type="email"
                                        value={editingCustomer.email}
                                        onChange={(e) => setEditingCustomer({ ...editingCustomer, email: e.target.value })}
                                      />
                                    </div>
                                  </div>
                                )}
                                <DialogFooter>
                                  <Button variant="outline" onClick={() => setIsEditCustomerOpen(false)}>Cancel</Button>
                                  <Button onClick={handleEditCustomer}>Save Changes</Button>
                                </DialogFooter>
                              </DialogContent>
                            </Dialog>
                            <Button size="icon" variant="ghost" onClick={() => handleDeleteCustomer(customer.id)}>
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Attendance Management Tab */}
        <TabsContent value="attendance" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Attendance Management</CardTitle>
                  <CardDescription>View and modify employee attendance records</CardDescription>
                </div>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline">
                      <Calendar className="h-4 w-4 mr-2" />
                      {format(selectedDate, 'PPP')}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <CalendarComponent
                      mode="single"
                      selected={selectedDate}
                      onSelect={(date) => date && setSelectedDate(date)}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </CardHeader>
            <CardContent>
              {isLoadingAttendance ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
                  <p className="text-sm text-muted-foreground">Loading attendance records...</p>
                </div>
              ) : attendanceRecords.length === 0 ? (
                <div className="text-center py-12">
                  <ClipboardCheck className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-lg font-medium mb-2">No attendance records</p>
                  <p className="text-muted-foreground">No attendance marked for {format(selectedDate, 'MMMM dd, yyyy')}</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Employee Name</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Check In</TableHead>
                        <TableHead>Check Out</TableHead>
                        <TableHead>Notes</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {attendanceRecords.map((record) => (
                        <TableRow key={record.id}>
                          <TableCell className="font-medium">{record.employee_name}</TableCell>
                          <TableCell>{getAttendanceBadge(record.status)}</TableCell>
                          <TableCell>{record.check_in || '-'}</TableCell>
                          <TableCell>{record.check_out || '-'}</TableCell>
                          <TableCell className="max-w-xs truncate">{record.notes || '-'}</TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setEditingAttendance(record);
                                  setIsEditAttendanceOpen(true);
                                }}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteAttendance(record.id)}
                              >
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Statistics Card */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="text-2xl font-bold">{attendanceRecords.length}</div>
                <p className="text-sm text-muted-foreground">Total Records</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="text-2xl font-bold text-green-600">
                  {attendanceRecords.filter(a => a.status === 'Present').length}
                </div>
                <p className="text-sm text-muted-foreground">Present</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="text-2xl font-bold text-red-600">
                  {attendanceRecords.filter(a => a.status === 'Absent').length}
                </div>
                <p className="text-sm text-muted-foreground">Absent</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="text-2xl font-bold text-blue-600">
                  {attendanceRecords.filter(a => a.status === 'Leave').length}
                </div>
                <p className="text-sm text-muted-foreground">On Leave</p>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Category Management Tab */}
        <TabsContent value="categories" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Category Management</CardTitle>
                  <CardDescription>Manage dashboard categories and sections</CardDescription>
                </div>
                <Dialog open={isAddCategoryOpen} onOpenChange={setIsAddCategoryOpen}>
                  <DialogTrigger asChild>
                    <Button className="shadow-emerald">
                      <Plus className="h-4 w-4 mr-2" />
                      Add Category
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Add New Category</DialogTitle>
                      <DialogDescription>Create a new dashboard category</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="space-y-2">
                        <Label htmlFor="cat-name">Category Name</Label>
                        <Input
                          id="cat-name"
                          placeholder="Enter category name"
                          value={newCategory.name}
                          onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="cat-desc">Description</Label>
                        <Input
                          id="cat-desc"
                          placeholder="Enter category description"
                          value={newCategory.description}
                          onChange={(e) => setNewCategory({ ...newCategory, description: e.target.value })}
                        />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setIsAddCategoryOpen(false)}>Cancel</Button>
                      <Button onClick={handleAddCategory}>Add Category</Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Category Name</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {categories.map((category) => (
                      <TableRow key={category.id}>
                        <TableCell className="font-medium">{category.name}</TableCell>
                        <TableCell className="text-muted-foreground">{category.description}</TableCell>
                        <TableCell>
                          <Switch
                            checked={category.enabled}
                            onCheckedChange={() => handleToggleCategoryStatus(category.id)}
                          />
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Dialog open={isEditCategoryOpen && editingCategory?.id === category.id} onOpenChange={(open) => {
                              setIsEditCategoryOpen(open);
                              if (!open) setEditingCategory(null);
                            }}>
                              <DialogTrigger asChild>
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  onClick={() => setEditingCategory(category)}
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                              </DialogTrigger>
                              <DialogContent>
                                <DialogHeader>
                                  <DialogTitle>Edit Category</DialogTitle>
                                  <DialogDescription>Update category details</DialogDescription>
                                </DialogHeader>
                                {editingCategory && (
                                  <div className="space-y-4 py-4">
                                    <div className="space-y-2">
                                      <Label htmlFor="edit-cat-name">Category Name</Label>
                                      <Input
                                        id="edit-cat-name"
                                        value={editingCategory.name}
                                        onChange={(e) => setEditingCategory({ ...editingCategory, name: e.target.value })}
                                      />
                                    </div>
                                    <div className="space-y-2">
                                      <Label htmlFor="edit-cat-desc">Description</Label>
                                      <Input
                                        id="edit-cat-desc"
                                        value={editingCategory.description}
                                        onChange={(e) => setEditingCategory({ ...editingCategory, description: e.target.value })}
                                      />
                                    </div>
                                  </div>
                                )}
                                <DialogFooter>
                                  <Button variant="outline" onClick={() => setIsEditCategoryOpen(false)}>Cancel</Button>
                                  <Button onClick={handleEditCategory}>Save Changes</Button>
                                </DialogFooter>
                              </DialogContent>
                            </Dialog>
                            <Button
                              size="icon"
                              variant="ghost"
                              onClick={() => handleDeleteCategory(category.id)}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* External Links Tab */}
        <TabsContent value="external-links" className="space-y-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>External Links</CardTitle>
                <CardDescription>
                  Manage redirect links shown in the sidebar (below Home). Drag rows to reorder. All users can access enabled links.
                </CardDescription>
              </div>
              <Button
                onClick={() => setIsAddExternalLinkOpen(true)}
                className="shadow-emerald"
                data-testid="add-external-link-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Link
              </Button>
            </CardHeader>
            <CardContent>
              {isLoadingExternalLinks ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
                  <p className="text-sm text-muted-foreground">Loading external links...</p>
                </div>
              ) : externalLinks.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No external links yet. Click "Add Link" to create one.
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10"></TableHead>
                      <TableHead>Label</TableHead>
                      <TableHead>URL</TableHead>
                      <TableHead>Icon</TableHead>
                      <TableHead>Position</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {externalLinks.map((link, index) => (
                      <TableRow
                        key={link.id}
                        data-testid={`external-link-row-${link.id}`}
                        draggable
                        onDragStart={() => handleLinkDragStart(index)}
                        onDragOver={(e) => handleLinkDragOver(e, index)}
                        onDragEnd={handleLinkDragEnd}
                        onDrop={(e) => handleLinkDrop(e, index)}
                        className={`
                          ${draggedLinkIndex === index ? 'opacity-40' : ''}
                          ${dragOverLinkIndex === index && draggedLinkIndex !== index ? 'bg-primary/10 border-t-2 border-primary' : ''}
                          cursor-move
                        `}
                      >
                        <TableCell className="text-muted-foreground">
                          <GripVertical className="h-4 w-4" />
                        </TableCell>
                        <TableCell className="font-medium">{link.label}</TableCell>
                        <TableCell className="max-w-xs truncate">
                          <a href={link.url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                            {link.url}
                          </a>
                        </TableCell>
                        <TableCell>{link.icon || 'Filter'}</TableCell>
                        <TableCell>{link.position}</TableCell>
                        <TableCell>
                          {link.enabled ? (
                            <Badge variant="default">Enabled</Badge>
                          ) : (
                            <Badge variant="outline">Disabled</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right space-x-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => {
                              setEditingExternalLink({ ...link });
                              setIsEditExternalLinkOpen(true);
                            }}
                            data-testid={`edit-external-link-${link.id}`}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDeleteExternalLink(link.id)}
                            data-testid={`delete-external-link-${link.id}`}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Settings Tab */}
        <TabsContent value="settings" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Application Settings</CardTitle>
              <CardDescription>Configure custom website link for sidebar</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {isLoadingSettings ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
                  <p className="text-sm text-muted-foreground">Loading settings...</p>
                </div>
              ) : (
                <>
                  <div className="space-y-4 max-w-2xl">
                    <div className="space-y-2">
                      <Label htmlFor="link-label">Custom Link Label</Label>
                      <Input
                        id="link-label"
                        placeholder="e.g., Our Website, Company Portal"
                        value={appSettings.custom_link_label}
                        onChange={(e) => setAppSettings({ ...appSettings, custom_link_label: e.target.value })}
                      />
                      <p className="text-xs text-muted-foreground">
                        This label will appear in the sidebar below Reports
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="link-url">Custom Link URL</Label>
                      <Input
                        id="link-url"
                        placeholder="https://example.com"
                        value={appSettings.custom_link_url}
                        onChange={(e) => setAppSettings({ ...appSettings, custom_link_url: e.target.value })}
                      />
                      <p className="text-xs text-muted-foreground">
                        Enter full URL including https://. Link will open in a new tab.
                      </p>
                    </div>

                    {appSettings.custom_link_label && appSettings.custom_link_url && (
                      <div className="bg-blue-50 dark:bg-blue-950 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
                        <p className="text-sm font-medium text-blue-900 dark:text-blue-100 mb-2">Preview:</p>
                        <div className="flex items-center gap-2 text-sm text-blue-700 dark:text-blue-300">
                          <span>Sidebar will show: "{appSettings.custom_link_label}"</span>
                        </div>
                        <div className="mt-1 text-xs text-blue-600 dark:text-blue-400">
                          Links to: {appSettings.custom_link_url}
                        </div>
                      </div>
                    )}

                    <Button onClick={handleSaveSettings} className="shadow-emerald">
                      Save Settings
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Edit Attendance Dialog */}
      <Dialog open={isEditAttendanceOpen} onOpenChange={setIsEditAttendanceOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Attendance Record</DialogTitle>
            <DialogDescription>
              Modify attendance status for {editingAttendance?.employee_name}
            </DialogDescription>
          </DialogHeader>
          {editingAttendance && (
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label>Employee Name</Label>
                <Input value={editingAttendance.employee_name} disabled />
              </div>
              <div className="grid gap-2">
                <Label>Date</Label>
                <Input value={editingAttendance.date} disabled />
              </div>
              <div className="grid gap-2">
                <Label>Status *</Label>
                <Select
                  value={editingAttendance.status}
                  onValueChange={(value) => setEditingAttendance({ ...editingAttendance, status: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Present">Present</SelectItem>
                    <SelectItem value="Absent">Absent</SelectItem>
                    <SelectItem value="Leave">Leave</SelectItem>
                    <SelectItem value="Half-day">Half-day</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label>Notes</Label>
                <Input
                  value={editingAttendance.notes || ''}
                  onChange={(e) => setEditingAttendance({ ...editingAttendance, notes: e.target.value })}
                  placeholder="Add notes (optional)"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditAttendanceOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleEditAttendance}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Password Dialog */}
      <Dialog open={isChangePasswordOpen} onOpenChange={setIsChangePasswordOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change Password</DialogTitle>
            <DialogDescription>
              Change password for {changingPasswordUser?.username}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label>Username</Label>
              <Input value={changingPasswordUser?.username || ''} disabled />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="new-password">New Password *</Label>
              <div className="relative">
                <Input
                  id="new-password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter new password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <p className="text-xs text-muted-foreground">
                Password must be at least 4 characters long
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setIsChangePasswordOpen(false);
                setNewPassword('');
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleChangePassword}>Change Password</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add External Link Dialog */}
      <Dialog open={isAddExternalLinkOpen} onOpenChange={setIsAddExternalLinkOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add External Link</DialogTitle>
            <DialogDescription>
              Add a sidebar redirect link. Visible to all users.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="new-ext-label">Label *</Label>
              <Input
                id="new-ext-label"
                placeholder="e.g., Data Filter"
                value={newExternalLink.label}
                onChange={(e) => setNewExternalLink({ ...newExternalLink, label: e.target.value })}
                data-testid="new-external-link-label"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="new-ext-url">URL *</Label>
              <Input
                id="new-ext-url"
                placeholder="https://example.com"
                value={newExternalLink.url}
                onChange={(e) => setNewExternalLink({ ...newExternalLink, url: e.target.value })}
                data-testid="new-external-link-url"
              />
              <p className="text-xs text-muted-foreground">Must start with http:// or https://</p>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="new-ext-icon">Icon</Label>
              <Select
                value={newExternalLink.icon}
                onValueChange={(v) => setNewExternalLink({ ...newExternalLink, icon: v })}
              >
                <SelectTrigger id="new-ext-icon">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Filter">Filter</SelectItem>
                  <SelectItem value="ExternalLink">External Link</SelectItem>
                  <SelectItem value="Calculator">Calculator</SelectItem>
                  <SelectItem value="Package">Package</SelectItem>
                  <SelectItem value="Box">Box</SelectItem>
                  <SelectItem value="Layers">Layers</SelectItem>
                  <SelectItem value="BookUser">Book/User</SelectItem>
                  <SelectItem value="FileSpreadsheet">Spreadsheet</SelectItem>
                  <SelectItem value="BarChart3">Chart</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="new-ext-position">Position</Label>
                <Input
                  id="new-ext-position"
                  type="number"
                  value={newExternalLink.position}
                  onChange={(e) => setNewExternalLink({ ...newExternalLink, position: parseInt(e.target.value, 10) || 0 })}
                />
                <p className="text-xs text-muted-foreground">Lower = higher in sidebar</p>
              </div>
              <div className="grid gap-2">
                <Label>Enabled</Label>
                <div className="flex items-center gap-2 h-10">
                  <Switch
                    checked={newExternalLink.enabled}
                    onCheckedChange={(v) => setNewExternalLink({ ...newExternalLink, enabled: v })}
                  />
                  <span className="text-sm text-muted-foreground">
                    {newExternalLink.enabled ? 'Visible to users' : 'Hidden from sidebar'}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddExternalLinkOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddExternalLink} data-testid="save-new-external-link">
              Add Link
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit External Link Dialog */}
      <Dialog open={isEditExternalLinkOpen} onOpenChange={setIsEditExternalLinkOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit External Link</DialogTitle>
            <DialogDescription>Modify the sidebar redirect link.</DialogDescription>
          </DialogHeader>
          {editingExternalLink && (
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label>Label *</Label>
                <Input
                  value={editingExternalLink.label}
                  onChange={(e) => setEditingExternalLink({ ...editingExternalLink, label: e.target.value })}
                />
              </div>
              <div className="grid gap-2">
                <Label>URL *</Label>
                <Input
                  value={editingExternalLink.url}
                  onChange={(e) => setEditingExternalLink({ ...editingExternalLink, url: e.target.value })}
                />
              </div>
              <div className="grid gap-2">
                <Label>Icon</Label>
                <Select
                  value={editingExternalLink.icon || 'Filter'}
                  onValueChange={(v) => setEditingExternalLink({ ...editingExternalLink, icon: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Filter">Filter</SelectItem>
                    <SelectItem value="ExternalLink">External Link</SelectItem>
                    <SelectItem value="Calculator">Calculator</SelectItem>
                    <SelectItem value="Package">Package</SelectItem>
                    <SelectItem value="Box">Box</SelectItem>
                    <SelectItem value="Layers">Layers</SelectItem>
                    <SelectItem value="BookUser">Book/User</SelectItem>
                    <SelectItem value="FileSpreadsheet">Spreadsheet</SelectItem>
                    <SelectItem value="BarChart3">Chart</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label>Position</Label>
                  <Input
                    type="number"
                    value={editingExternalLink.position ?? 0}
                    onChange={(e) => setEditingExternalLink({ ...editingExternalLink, position: parseInt(e.target.value, 10) || 0 })}
                  />
                </div>
                <div className="grid gap-2">
                  <Label>Enabled</Label>
                  <div className="flex items-center gap-2 h-10">
                    <Switch
                      checked={!!editingExternalLink.enabled}
                      onCheckedChange={(v) => setEditingExternalLink({ ...editingExternalLink, enabled: v })}
                    />
                    <span className="text-sm text-muted-foreground">
                      {editingExternalLink.enabled ? 'Visible to users' : 'Hidden from sidebar'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditExternalLinkOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdateExternalLink}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPanel;